import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Loader2, AlertTriangle, Trash2, UserCheck } from 'lucide-react';
import { Modal, Badge } from '../ui-v2.5';
import { Button } from '../ui-v2';
import PersonaPickerMiembro from './components/PersonaPickerMiembro';
import {
  listPersonasDelPuesto, vincularPersonaAPuesto, desvincularPersonaDePuesto, alcanceDePersona,
} from './api';

/**
 * QuienDaDeAltaModal — quién puede dar de alta en un puesto.
 *
 * ── LO QUE ESTA PANTALLA TIENE QUE DEJAR CLARO ────────────────────────────────────────────────
 *
 * El vínculo **limita, no concede**. Añadir a alguien aquí NO le da permiso para crear altas —eso
 * se da en `persona_permisos`, aparte— sino que le RECORTA a estos puestos. Y el recorte es por
 * adhesión:
 *
 *     sin vínculos  ->  ve el embudo entero y crea en cualquier puesto
 *     con uno       ->  solo sus puestos, y solo ve las altas que ha creado él
 *
 * ── POR QUÉ AVISA EN LOS DOS SENTIDOS ─────────────────────────────────────────────────────────
 *
 * Con alcance por adhesión el comportamiento NO cambia al añadir el tercer vínculo: cambia en
 * **0→1** y en **1→0**. Las dos son el mismo cambio de estado, visto por cada lado:
 *
 *     0 -> 1   esa persona DEJA de ver el embudo entero. Se avisa antes de guardar.
 *     1 -> 0   esa persona LO RECUPERA. Se avisa también, porque quien lo quita puede no saber
 *              que era el último — desde aquí solo se ve este puesto, no los demás.
 *
 * El segundo aviso es el que casi no se escribe, y es el que más sorprende después: se quita un
 * vínculo «de este puesto» y lo que ocurre es que alguien recupera acceso a todo.
 *
 * Los dos números los da el SERVIDOR (`vinculos_totales`, `era_su_primer_vinculo`,
 * `era_su_ultimo_vinculo`): esta pantalla ve un puesto, y la pregunta es sobre todos.
 */

export default function QuienDaDeAltaModal({ puesto, onClose }) {
  const [personas, setPersonas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  // { persona, vinculos } — la confirmación del PRIMER vínculo
  const [confirmarPrimero, setConfirmarPrimero] = useState(null);
  // { persona_id, nombre } — la confirmación del ÚLTIMO
  const [confirmarUltimo, setConfirmarUltimo] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setPersonas(await listPersonasDelPuesto(puesto.id));
    } catch (e) {
      console.error('[incorp][quien-da-de-alta] cargar', e);
      /* El catch DICE algo: una lista vacía por un fallo de red y una lista vacía porque nadie
       * está vinculado se ven igual, y aquí la diferencia importa — la segunda significa «este
       * puesto no restringe a nadie» y la primera no significa nada. */
      setError((e && e.response && e.response.data && e.response.data.message)
        || 'No se pudo cargar quién puede dar de alta en este puesto.');
    } finally {
      setCargando(false);
    }
  }, [puesto.id]);

  useEffect(() => { cargar(); }, [cargar]);

  /* Al elegir a alguien NO se guarda todavía: primero se pregunta al servidor cuántos vínculos
   * tiene ya, porque de eso depende si esto le cambia el comportamiento. Un aviso después de
   * guardar no es un aviso, es una noticia. */
  async function alElegir(persona) {
    setGuardando(true);
    try {
      const a = await alcanceDePersona(persona.id);
      if (a.vinculos === 0) { setConfirmarPrimero({ persona, vinculos: 0 }); return; }
      await vincular(persona);
    } catch (e) {
      console.error('[incorp][quien-da-de-alta] alElegir', e);
      toast.error((e && e.response && e.response.data && e.response.data.message)
        || 'No se pudo comprobar el alcance de esa persona.');
    } finally {
      setGuardando(false);
    }
  }

  async function vincular(persona) {
    setGuardando(true);
    try {
      const r = await vincularPersonaAPuesto(puesto.id, persona.id);
      toast.success(r.era_su_primer_vinculo
        ? `${persona.nombre} queda acotada: a partir de ahora solo da de alta en sus puestos.`
        : `${persona.nombre} ya puede dar de alta en «${puesto.nombre}».`);
      setConfirmarPrimero(null);
      await cargar();
    } catch (e) {
      console.error('[incorp][quien-da-de-alta] vincular', e);
      toast.error((e && e.response && e.response.data && e.response.data.message) || 'No se pudo vincular.');
    } finally {
      setGuardando(false);
    }
  }

  async function quitar(p) {
    setGuardando(true);
    try {
      const r = await desvincularPersonaDePuesto(puesto.id, p.persona_id);
      toast.success(r.era_su_ultimo_vinculo
        ? `${p.nombre} vuelve a ver el embudo entero y a poder dar de alta en cualquier puesto.`
        : `${p.nombre} ya no da de alta en «${puesto.nombre}».`);
      setConfirmarUltimo(null);
      await cargar();
    } catch (e) {
      console.error('[incorp][quien-da-de-alta] quitar', e);
      toast.error((e && e.response && e.response.data && e.response.data.message) || 'No se pudo desvincular.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title="Quién puede dar de alta aquí"
      subtitle={puesto.nombre}
      footer={<Button variant="outline" onClick={onClose} disabled={guardando}>Cerrar</Button>}
    >
      <div className="space-y-4">
        {/* LO QUE ESTA PANTALLA HACE Y LO QUE NO. Sin esto, «añadir a alguien a un puesto» se lee
            como darle permiso, que es justo lo contrario. */}
        <div className="rounded-v25-md border border-v25-line bg-v25-ok-bg p-3 text-[13px] leading-relaxed text-v25-ink-900">
          Esto <strong>no da permiso</strong> para crear altas: lo <strong>acota</strong>. Quien no
          tenga ningún puesto asignado sigue viendo el embudo entero y puede dar de alta en
          cualquiera. En cuanto tiene uno, pasa a ver <strong>solo las altas que ha creado</strong>
          {' '}y a dar de alta <strong>solo en sus puestos</strong>.
        </div>

        {cargando ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-v25-ink-500">
            <Loader2 className="h-5 w-5 animate-spin" /> Cargando…
          </div>
        ) : error ? (
          <div className="rounded-v25-md border border-v25-expired-bd bg-v25-expired-bg p-3 text-[13px] text-v25-expired-fg">
            {error}
          </div>
        ) : (
          <>
            {personas.length === 0 ? (
              <p className="text-[13px] text-v25-ink-500">
                Nadie está asignado a este puesto, así que no acota a nadie.
              </p>
            ) : (
              <ul className="divide-y divide-v25-line rounded-v25-md border border-v25-line">
                {personas.map((p) => (
                  <li key={p.persona_id} className="flex items-center justify-between gap-2 p-2.5">
                    <div className="min-w-0">
                      <span className="block truncate text-sm text-v25-ink-900">{p.nombre}</span>
                      {/* El número total, visible: desde aquí solo se ve ESTE puesto, y la
                          consecuencia de quitarlo depende de cuántos tenga en los demás. */}
                      <span className="text-xs text-v25-ink-500">
                        {p.vinculos_totales === 1
                          ? 'Éste es su único puesto'
                          : `${p.vinculos_totales} puestos asignados`}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {p.es_su_unico_vinculo && <Badge tone="received">último</Badge>}
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={guardando}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => (p.es_su_unico_vinculo
                          ? setConfirmarUltimo(p)
                          : quitar(p))}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <PersonaPickerMiembro
              onAdd={alElegir}
              excluirIds={personas.map((p) => p.persona_id)}
              disabled={guardando}
              enFlujo
              placeholder="Añadir persona…"
            />
          </>
        )}

        {/* ── EL AVISO DEL PRIMERO: 0 -> 1 ───────────────────────────────────────────────── */}
        {confirmarPrimero && (
          <div className="rounded-v25-md border border-v25-received-bd bg-v25-received-bg p-3 text-[13px] leading-relaxed text-v25-received-fg">
            <div className="mb-2 flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong>{confirmarPrimero.persona.nombre}</strong> no tiene ningún puesto asignado
                todavía, así que hoy <strong>ve todas las altas</strong> y puede dar de alta en
                cualquier puesto. Si la añades aquí, pasará a ver <strong>solo las altas que haya
                creado ella</strong> y a dar de alta <strong>solo en «{puesto.nombre}»</strong>.
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" disabled={guardando} onClick={() => setConfirmarPrimero(null)}>
                Cancelar
              </Button>
              <Button size="sm" disabled={guardando} onClick={() => vincular(confirmarPrimero.persona)}>
                {guardando ? <Loader2 className="animate-spin" /> : <UserCheck />}Acotarla a este puesto
              </Button>
            </div>
          </div>
        )}

        {/* ── EL AVISO DEL ÚLTIMO: 1 -> 0. El que casi no se escribe. ────────────────────── */}
        {confirmarUltimo && (
          <div className="rounded-v25-md border border-v25-expired-bd bg-v25-expired-bg p-3 text-[13px] leading-relaxed text-v25-expired-fg">
            <div className="mb-2 flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Éste es el <strong>único puesto</strong> de <strong>{confirmarUltimo.nombre}</strong>.
                Si lo quitas deja de estar acotada: volverá a <strong>ver el embudo entero</strong> y
                a poder dar de alta <strong>en cualquier puesto</strong>.
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" disabled={guardando} onClick={() => setConfirmarUltimo(null)}>
                Cancelar
              </Button>
              <Button size="sm" disabled={guardando} onClick={() => quitar(confirmarUltimo)}>
                {guardando ? <Loader2 className="animate-spin" /> : null}Quitarlo y devolverle el acceso completo
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
